// Phase 4 — Sandbox dampening
//
// Lightweight module-level flag the Sandbox UI sets when impersonation
// becomes active. Write paths that should be inert while the Architect is
// simulating another user can call `assertNotSandbox(scope)` to short-circuit.
//
// The default policy is permissive: sandbox does not block writes. Callers
// opt in by checking `isSandboxActive()` or wrapping with
// `assertNotSandbox()`.

let active = false;
let label: string | null = null;

export function setSandboxActive(next: boolean, nextLabel: string | null = null) {
  active = next;
  label = next ? nextLabel : null;
  try {
    window.dispatchEvent(new CustomEvent("workitect-sandbox-active-changed", {
      detail: { active: next, label: nextLabel },
    }));
  } catch { /* noop */ }
}

export function isSandboxActive(): boolean {
  return active;
}

export function getSandboxLabel(): string | null {
  return label;
}

export class SandboxBlockedError extends Error {
  readonly scope: string;
  constructor(scope: string) {
    super(`Blocked in sandbox mode: ${scope}`);
    this.name = "SandboxBlockedError";
    this.scope = scope;
  }
}

/**
 * Throw `SandboxBlockedError` when sandbox is active. Use at the top of
 * destructive write paths that must not hit production data while the
 * Architect is impersonating.
 */
export function assertNotSandbox(scope: string): void {
  if (active) throw new SandboxBlockedError(scope);
}
